import { Fragment, useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { usePrevious } from 'react-use';

import {
  Grid,
  FormLabel,
  VisuallyHidden,
  FormControl,
  Input,
  IconButton,
  CloseIcon,
  InputGroup,
  InputLeftAddon,
} from '@frontend/legacy-components';

import { useAppSelector } from 'store/hooks';
import { ActionPayload } from 'store/types';

import { additionalCharges } from '../data/actions';
import { getAdditionalChargesMap } from '../data/reducer';

type AdditionalChargeProps = {
  quoteItemId: number;
};

export const AdditionalCharges = ({ quoteItemId }: AdditionalChargeProps) => {
  const dispatch = useDispatch();
  const additionalChargesMap = useAppSelector((state) =>
    getAdditionalChargesMap(state, quoteItemId),
  );
  const previousAdditionalChargesMap = usePrevious(additionalChargesMap);
  const shouldFocusLastInput = useMemo(
    () =>
      previousAdditionalChargesMap &&
      Object.keys(additionalChargesMap).length >
        Object.keys(previousAdditionalChargesMap).length,
    [additionalChargesMap, previousAdditionalChargesMap],
  );
  const deleteAdditionalCharge = useCallback(
    (data: ActionPayload<typeof additionalCharges.delete>) => {
      dispatch(additionalCharges.delete(data));
    },
    [dispatch],
  );
  const updateAdditionalCharge = useCallback(
    (data: ActionPayload<typeof additionalCharges.update>) => {
      dispatch(additionalCharges.update(data));
    },
    [dispatch],
  );

  const [errors, setErrors] = useState<Record<string, Record<string, boolean>>>(
    {},
  );

  if (Object.keys(additionalChargesMap).length === 0) return null;

  return (
    <Grid
      as="fieldset"
      gridTemplateColumns="1fr 200px min-content"
      alignItems="center"
      gap={1}
      paddingY={1.5}
      paddingX={2}
    >
      <FormLabel as="span" mb={-0.5}>
        Additional Charge
      </FormLabel>
      <FormLabel as="span" mb={-0.5} gridColumn="2 / span 2">
        Price
      </FormLabel>
      {Object.values(additionalChargesMap).map((charge: any, index) => (
        <Fragment key={charge.id}>
          <FormControl isInvalid={errors[charge.id]?.nameError} isRequired>
            <FormLabel as={VisuallyHidden}>Additional Charge</FormLabel>
            <Input
              autoFocus={
                shouldFocusLastInput &&
                index === Object.values(additionalChargesMap).length - 1
              }
              onBlur={(e) => {
                const value = e.target.value;
                if (!value || value.trim() === '') {
                  setErrors({
                    ...errors,
                    [charge.id]: {
                      ...errors[charge.id],
                      nameError: true,
                    },
                  });
                }
                updateAdditionalCharge({
                  additionalChargeId: charge.id,
                  quoteItemId: quoteItemId,
                  data: {
                    name: value,
                  },
                });
              }}
              onChange={() => {
                if (errors[charge.id]?.nameError) {
                  setErrors({
                    ...errors,
                    [charge.id]: {
                      ...errors[charge.id],
                      nameError: false,
                    },
                  });
                }
              }}
              placeholder="E.g. Non-recurring Engineering"
              defaultValue={charge.name}
            />
          </FormControl>
          <FormControl isInvalid={errors[charge.id]?.priceError} isRequired>
            <FormLabel as={VisuallyHidden}>Price</FormLabel>
            <InputGroup>
              <InputLeftAddon>$</InputLeftAddon>
              <Input
                textAlign="right"
                defaultValue={charge.price}
                onBlur={(e) => {
                  const value = e.target.value;
                  if (isNaN(parseFloat(value)) || value === '') {
                    setErrors({
                      ...errors,
                      [charge.id]: {
                        ...errors[charge.id],
                        priceError: true,
                      },
                    });
                  }
                  updateAdditionalCharge({
                    additionalChargeId: charge.id,
                    quoteItemId: quoteItemId,
                    data: {
                      price: value,
                    },
                  });
                }}
                onChange={(e) => {
                  const value = e.target.value;
                  // if value contains non numbers or dot, remove them
                  if (/[^0-9.]/g.test(value)) {
                    e.target.value = value.replace(/[^0-9.]/g, '');
                  }

                  if (errors[charge.id]?.priceError) {
                    setErrors({
                      ...errors,
                      [charge.id]: {
                        ...errors[charge.id],
                        priceError: false,
                      },
                    });
                  }
                }}
              />
            </InputGroup>
          </FormControl>
          <IconButton
            aria-label="Delete additional charge"
            size="sm"
            onClick={() => {
              deleteAdditionalCharge({
                additionalChargeId: charge.id,
                quoteItemId: quoteItemId,
              });
            }}
          >
            <CloseIcon />
          </IconButton>
        </Fragment>
      ))}
    </Grid>
  );
};
